import React, { useRef, useState, useCallback } from 'react';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';

const HANDLE_H = 20;
const CONTROLS_H = 64;
const FILTER_H = 280;   // 3 filter rows + speed row + 1 toggle + reader note + separators
const EXPANDED_H = HANDLE_H + CONTROLS_H + FILTER_H; // 364px total
const COLLAPSE_THRESHOLD = 60;

// Horizontal-scroll pill row — no dropdown, no z-index issues
function FilterRow({ label, options, value, onChange }) {
  return (
    <div className="flex items-center gap-2 min-h-[32px]">
      <span className="text-[10px] text-gray-400 dark:text-white/35 w-14 flex-shrink-0 font-semibold uppercase tracking-wider leading-tight">
        {label}
      </span>
      <div className="flex gap-1.5 overflow-x-auto flex-1 pb-0.5" style={{ scrollbarWidth: 'none' }}>
        {options.map(opt => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap active:scale-95 ${
              opt.value === value
                ? 'bg-cerulean text-white'
                : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white/60 hover:bg-gray-200 dark:hover:bg-white/15'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Readers row — multi-select pills; readers without recordings in the current
// chapter are grayed out (still selectable — selection carries across chapters)
function ReadersRow({ selectedReaders, availableReaders, chapterReaderIds, onSetSelectedReaders }) {
  const noneSelected = selectedReaders.length === 0;

  function toggle(id) {
    if (id === 'all') { onSetSelectedReaders([]); return; }
    if (selectedReaders.includes(id)) {
      onSetSelectedReaders(selectedReaders.filter(r => r !== id));
    } else {
      onSetSelectedReaders([...selectedReaders, id]);
    }
  }

  // Available readers first, grayed-out ones after
  const sortedReaders = [...availableReaders].sort((a, b) => {
    const aHas = chapterReaderIds.has(a.id) ? 0 : 1;
    const bHas = chapterReaderIds.has(b.id) ? 0 : 1;
    return aHas - bHas;
  });

  return (
    <div className="flex items-center gap-2 min-h-[32px]">
      <span className="text-[10px] text-gray-400 dark:text-white/35 w-14 flex-shrink-0 font-semibold uppercase tracking-wider leading-tight">
        Readers
      </span>
      <div className="flex gap-1.5 overflow-x-auto flex-1 pb-0.5" style={{ scrollbarWidth: 'none' }}>
        <button
          onClick={() => toggle('all')}
          className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap active:scale-95 ${
            noneSelected ? 'bg-cerulean text-white' : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white/60 hover:bg-gray-200 dark:hover:bg-white/15'
          }`}
        >
          All
        </button>
        {sortedReaders.map(reader => {
          const checked = selectedReaders.includes(reader.id);
          const hasVerses = chapterReaderIds.has(reader.id);
          return (
            <button
              key={reader.id}
              onClick={() => toggle(reader.id)}
              className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap active:scale-95 ${
                checked
                  ? hasVerses ? 'bg-cerulean text-white' : 'bg-cerulean/40 text-white/80'
                  : hasVerses ? 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-white/60 hover:bg-gray-200 dark:hover:bg-white/15' : 'bg-gray-50 dark:bg-white/5 text-gray-300 dark:text-white/20'
              }`}
            >
              {reader.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Compose the listener-facing note about what will actually play this chapter
function buildReaderNote(selectedReaders, availableReaders, chapterReaderIds) {
  const nameOf = id => availableReaders.find(r => r.id === id)?.name || 'Unknown';
  const listNames = arr =>
    arr.length <= 1 ? arr[0] : `${arr.slice(0, -1).join(', ')} and ${arr[arr.length - 1]}`;

  if (chapterReaderIds.size === 0) {
    return 'No recordings in this chapter yet.';
  }

  if (selectedReaders.length === 0) {
    const names = availableReaders.filter(r => chapterReaderIds.has(r.id)).map(r => r.name);
    return `This chapter has recordings by ${listNames(names)}.`;
  }

  const withVerses = selectedReaders.filter(id => chapterReaderIds.has(id)).map(nameOf);
  const withoutVerses = selectedReaders.filter(id => !chapterReaderIds.has(id)).map(nameOf);

  if (withVerses.length === 0) {
    return `${listNames(withoutVerses)} ${withoutVerses.length > 1 ? 'have' : 'has'} no recordings in this chapter — playing all readers.`;
  }
  let note = `You will hear verses read by ${listNames(withVerses)}.`;
  if (withoutVerses.length > 0) {
    note += ` ${listNames(withoutVerses)} ${withoutVerses.length > 1 ? 'have' : 'has'} no recordings in this chapter.`;
  }
  return note;
}

// Toggle row
function SheetToggle({ label, description, icon, value, onChange }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className="w-full flex items-center justify-between gap-3 py-2 active:bg-gray-50 dark:active:bg-white/5 rounded-xl px-1 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-gray-100 dark:bg-white/10 flex items-center justify-center">
          {icon}
        </div>
        <div className="text-left min-w-0">
          <div className="text-sm font-medium text-space-cadet dark:text-white leading-tight">{label}</div>
          {description && <div className="text-xs text-gray-400 dark:text-white/35 leading-tight mt-0.5 truncate">{description}</div>}
        </div>
      </div>
      <div className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${value ? 'bg-cerulean' : 'bg-gray-300 dark:bg-white/15'}`}>
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${value ? 'translate-x-4' : 'translate-x-0'}`} />
      </div>
    </button>
  );
}

const SPEED_OPTIONS = [0.75, 0.9, 1.0, 1.1, 1.25, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9, 2.0].map(s => ({
  value: s,
  label: s === 1.0 ? '1×' : `${s}×`,
}));

const BottomPlayerSheet = ({
  isExpanded,
  onExpandChange,
  isPlaying,
  hasRecordings,
  onPlay,
  onPrev,
  onNext,
  selectedFamily,
  selectedReaders,
  selectedPlan,
  userFamilies,
  availableReaders,
  availableListeningPlans,
  chapterReaderIds,
  trimSilence,
  onSetTrimSilence,
  onSetSelectedFamily,
  onSetSelectedReaders,
  onSetSelectedPlan,
  playbackRate,
  onSetPlaybackRate,
}) => {
  const touchStartY = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  const readerIds = chapterReaderIds || new Set();
  const readerNote = buildReaderNote(selectedReaders || [], availableReaders || [], readerIds);

  const familyOptions = [
    { value: 'all', label: 'All' },
    ...(userFamilies || []).map(f => ({ value: f.id, label: f.name })),
  ];
  const planOptions = [
    { value: 'all', label: 'All' },
    ...(availableListeningPlans || []).map(p => ({ value: p.id, label: p.name })),
  ];

  const handleTouchStart = useCallback((e) => {
    touchStartY.current = e.touches[0].clientY;
    setDragging(true);
    setDragOffset(0);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (touchStartY.current === null) return;
    setDragOffset(e.touches[0].clientY - touchStartY.current);
  }, []);

  const handleTouchEnd = useCallback(() => {
    const delta = dragOffset;
    setDragging(false);
    setDragOffset(0);
    touchStartY.current = null;
    if (delta > COLLAPSE_THRESHOLD) onExpandChange(false);
  }, [dragOffset, onExpandChange]);

  // Collapsed: just floating circle controls above the bottom nav (mobile)
  // or floating above the page footer (desktop, no bottom nav there)
  if (!isExpanded) {
    return (
      <div key="collapsed-controls" className="fixed bottom-24 lg:bottom-6 left-1/2 -translate-x-1/2 z-[9990] flex items-center gap-3">
        <button
          onClick={() => onExpandChange(true)}
          className="w-10 h-10 rounded-full bg-[#444] dark:bg-[#1a2332] dark:border dark:border-white/15 shadow-lg flex items-center justify-center text-white active:scale-95 transition-transform"
          aria-label="Listening settings"
        >
          <AdjustmentsHorizontalIcon className="w-5 h-5" />
        </button>
        <button
          onClick={onPrev}
          className="w-10 h-10 rounded-full bg-[#444] dark:bg-[#1a2332] dark:border dark:border-white/15 shadow-lg flex items-center justify-center text-white active:scale-95 transition-transform"
          aria-label="Previous chapter"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        {hasRecordings && (
          <button
            onClick={onPlay}
            className="w-14 h-14 rounded-full bg-[#444] dark:bg-[#1a2332] dark:border dark:border-white/15 shadow-lg flex items-center justify-center text-white active:scale-95 transition-transform"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            ) : (
              <svg className="w-6 h-6 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
        )}
        <button
          onClick={onNext}
          className="w-10 h-10 rounded-full bg-[#444] dark:bg-[#1a2332] dark:border dark:border-white/15 shadow-lg flex items-center justify-center text-white active:scale-95 transition-transform"
          aria-label="Next chapter"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    );
  }

  // Expanded: full sheet with controls + filters, swipe down to collapse.
  // Full-bleed on mobile; centered as a fixed-width card on wider screens.
  return (
    <div
      key="expanded-sheet"
      className="fixed left-0 right-0 lg:left-1/2 lg:right-auto lg:-ml-[210px] lg:w-[420px] bottom-20 lg:bottom-6 z-[9990]"
      style={{
        height: EXPANDED_H,
        transform: `translateY(${Math.max(0, dragOffset)}px)`,
        transition: dragging ? 'none' : 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)',
        willChange: 'transform',
        animation: 'sheet-rise 0.35s cubic-bezier(0.32, 0.72, 0, 1)',
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <style>{`
        @keyframes sheet-rise {
          from { transform: translateY(${EXPANDED_H}px); }
          to { transform: translateY(0); }
        }
      `}</style>
      <div className="h-full bg-white dark:bg-[#111827] rounded-t-2xl lg:rounded-2xl border-t lg:border border-gray-200 dark:border-white/10 flex flex-col" style={{ boxShadow: '0 -8px 24px rgba(0,0,0,0.12)' }}>
        {/* Drag handle */}
        <div
          className="flex justify-center pt-3 pb-1 cursor-pointer flex-shrink-0"
          style={{ height: HANDLE_H }}
          onClick={() => onExpandChange(false)}
        >
          <div className="w-8 h-1 bg-gray-300 dark:bg-white/20 rounded-full mt-1" />
        </div>

        {/* Controls */}
        <div className="flex-shrink-0 flex items-center justify-between px-8" style={{ height: CONTROLS_H }}>
          <button onClick={onPrev} className="p-3 text-gray-400 dark:text-white/40 hover:text-space-cadet dark:hover:text-white active:scale-90 transition-all" aria-label="Previous chapter">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 6h2v12H6zm3.5 6 8.5 6V6z" />
            </svg>
          </button>

          <button
            onClick={onPlay}
            disabled={!hasRecordings}
            className="w-[52px] h-[52px] rounded-full bg-cerulean flex items-center justify-center shadow-lg active:scale-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
            ) : (
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" style={{ marginLeft: 3 }}>
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          <button onClick={onNext} className="p-3 text-gray-400 dark:text-white/40 hover:text-space-cadet dark:hover:text-white active:scale-90 transition-all" aria-label="Next chapter">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 18l8.5-6L6 6v12zm2.5-6 5.5 3.9V8.1L8.5 12zM16 6h2v12h-2z" />
            </svg>
          </button>
        </div>

        {/* Filters + toggles */}
        <div className="flex-shrink-0 px-4" style={{ height: FILTER_H }}>
          <div className="border-t border-gray-100 dark:border-white/10 mb-3" />

          <div className="space-y-2.5 mb-3">
            <FilterRow
              label="Family"
              options={familyOptions}
              value={selectedFamily}
              onChange={onSetSelectedFamily}
            />
            <ReadersRow
              selectedReaders={selectedReaders}
              availableReaders={availableReaders || []}
              chapterReaderIds={readerIds}
              onSetSelectedReaders={onSetSelectedReaders}
            />
            <FilterRow
              label="Plan"
              options={planOptions}
              value={selectedPlan}
              onChange={onSetSelectedPlan}
            />
            <FilterRow
              label="Speed"
              options={SPEED_OPTIONS}
              value={playbackRate ?? 1.0}
              onChange={onSetPlaybackRate}
            />
          </div>

          <div className="border-t border-gray-100 dark:border-white/10 mb-1" />

          <SheetToggle
            label="Trim silence"
            description="Skip dead air at start and end"
            value={trimSilence}
            onChange={onSetTrimSilence}
            icon={
              <svg className="w-4 h-4 text-gray-500 dark:text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            }
          />
          {readerNote && (
            <p className="text-[10px] text-gray-400 dark:text-white/35 leading-snug px-1 mt-1">
              {readerNote}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BottomPlayerSheet;
